/**
 * The seats a workspace can hand out, widest first.
 *
 * Five of these are about how much of the workspace a person runs; two are
 * about what kind of person they are. `contractor` is somebody outside the
 * company doing work inside it — a freelancer with a login, who logs time and
 * sees none of the money. `viewer` is somebody who needs to read and must
 * never write: a stakeholder, a director, a client-side observer.
 *
 * A role is the whole authorisation story: it travels in the auth claim and
 * every rule reads it from there. Adding one here means adding it to
 * firestore.rules in the same commit, because the two are one decision.
 */
/**
 * `custom` is not a role with fixed powers: it means "see this member's
 * customRoleId, and the capability list the claim carries for them". Every
 * fixed-role list below therefore excludes it on purpose — a custom role is
 * never an admin by accident.
 */
export const ROLES = ['owner', 'admin', 'manager', 'member', 'accountant', 'contractor', 'viewer', 'custom'] as const;
export type Role = (typeof ROLES)[number];

/** Roles that may administer the org. Mirrors isAdmin() in firestore.rules. */
export const ADMIN_ROLES: readonly Role[] = ['owner', 'admin'];
/** Mirrors isManagerUp() in firestore.rules. */
export const MANAGER_UP_ROLES: readonly Role[] = ['owner', 'admin', 'manager'];
/** Who sees money: rates, invoices, pay. An accountant is otherwise an ordinary member. */
export const FINANCE_ROLES: readonly Role[] = ['owner', 'admin', 'accountant'];
/**
 * Who may change anything at all. Mirrors canWrite() in firestore.rules, which
 * refuses a viewer the same way it refuses a suspended workspace: not by
 * hiding the buttons, but by rejecting the write.
 */
export const READ_ONLY_ROLES: readonly Role[] = ['viewer'];
/**
 * People on the payroll, as against people on a purchase order. The
 * distinction is not a permission — a contractor's permissions are listed
 * above — it is who the workspace pays and how their hours are priced: a
 * contractor's hours cost their pay rate and bill at the project rate, and
 * the difference is the agency's margin.
 */
export const EXTERNAL_ROLES: readonly Role[] = ['contractor'];
/**
 * Roles that run the work rather than only doing it: creating tasks, filing
 * clients, keeping templates. A contractor works on what they are given.
 */
export const STAFF_ROLES: readonly Role[] = ['owner', 'admin', 'manager', 'member', 'accountant'];
/**
 * What an admin can pick in the people screens. Ownership is transferred, not
 * assigned, so it is not on the list.
 */
export const ASSIGNABLE_ROLES: readonly Role[] = ['member', 'manager', 'admin', 'accountant', 'contractor', 'viewer'];

export function isExternalRole(role: Role): boolean {
  return EXTERNAL_ROLES.includes(role);
}

export function isReadOnlyRole(role: Role): boolean {
  return READ_ONLY_ROLES.includes(role);
}

/** Fixed platform-wide. Per-tenant statuses are explicitly out of scope. */
export const TASK_STATUSES = ['todo', 'in_progress', 'blocked', 'review', 'done'] as const;
export type TaskStatus = (typeof TASK_STATUSES)[number];

export const TASK_PRIORITIES = ['none', 'low', 'normal', 'high', 'urgent'] as const;
export type TaskPriority = (typeof TASK_PRIORITIES)[number];

/** Depth 0 task, depth 1 subtask, depth 2 sub-subtask. Depth 3 is rejected. */
export const MAX_TASK_DEPTH = 2;

export const ORG_STATUSES = ['trial', 'active', 'suspended'] as const;
export type OrgStatus = (typeof ORG_STATUSES)[number];

export const MEMBER_STATUSES = ['active', 'deactivated'] as const;
export type MemberStatus = (typeof MEMBER_STATUSES)[number];

export const INVITE_STATUSES = ['pending', 'accepted', 'expired', 'revoked'] as const;
export type InviteStatus = (typeof INVITE_STATUSES)[number];

export const LEAVE_REQUEST_STATUSES = ['pending', 'approved', 'rejected', 'cancelled'] as const;
export type LeaveRequestStatus = (typeof LEAVE_REQUEST_STATUSES)[number];

export const DAY_PORTIONS = ['full', 'half_am', 'half_pm'] as const;
export type DayPortion = (typeof DAY_PORTIONS)[number];

export const DAY_PORTION_VALUE: Record<DayPortion, number> = {
  full: 1,
  half_am: 0.5,
  half_pm: 0.5,
};

export const TIME_ENTRY_SOURCES = ['timer', 'manual'] as const;
export type TimeEntrySource = (typeof TIME_ENTRY_SOURCES)[number];

export const COMMENT_STATES = ['active', 'edited', 'deleted'] as const;
export type CommentState = (typeof COMMENT_STATES)[number];

export const CARRYOVER_MODES = ['expire', 'full', 'capped'] as const;
export type CarryoverMode = (typeof CARRYOVER_MODES)[number];

export const DEFAULT_TIMEZONE = 'Europe/Athens';
/** A week of everything unlocked, no card. Long enough to log a real week of work, short enough to stay a decision. */
export const TRIAL_DAYS = 7;
/** A running timer past this is auto-stopped and capped at this length. */
export const TIMER_MAX_MINUTES = 12 * 60;
export const INVITE_TTL_DAYS = 14;
/** Idempotency records for trigger events are kept this long. */
export const PROCESSED_EVENT_TTL_DAYS = 7;
export const PAGE_SIZE = 50;

export const NOTIFICATION_TYPES = [
  'invitation',
  'welcome',
  'leave_request_submitted',
  'leave_decided',
  'year_end_balance',
  'mention',
  'task_assigned',
  'task_completed',
  'scheduled_report',
  'trial_expiry_warning',
  'project_budget',
  'capacity_changed',
  'task_due',
  'task_watched',
  'weekly_digest',
  'task_imported',
  'client_retainer',
  'invoice_overdue',
  'quote_accepted',
  'timer_auto_stopped',
  'contract_notice',
  'timer_handed',
  'leave_cover',
  'entry_notes_requested',
] as const;
export type NotificationType = (typeof NOTIFICATION_TYPES)[number];

/** Notification types that ignore the per-user non-critical opt-out. */
export const CRITICAL_NOTIFICATION_TYPES: readonly NotificationType[] = [
  'invitation',
  // Someone who has just been given an account has not had the chance to opt
  // out of anything, and without this they never learn the account exists.
  'welcome',
  'leave_decided',
  // A force-stopped timer left a capped 12-hour entry that is almost
  // certainly wrong. The person can only correct it if they hear about it.
  'timer_auto_stopped',
];

export const DEFAULT_LEAVE_TYPES = [
  { id: 'annual', name: 'Annual', deductsFromBalance: true, archived: false },
  { id: 'sick', name: 'Sick', deductsFromBalance: false, archived: false },
  { id: 'unpaid', name: 'Unpaid', deductsFromBalance: false, archived: false },
  { id: 'parental', name: 'Parental', deductsFromBalance: false, archived: false },
  { id: 'other', name: 'Other', deductsFromBalance: false, archived: false },
] as const;
